import { el, mount, unmount } from 'redom';

import application from 'application';

import internalEventCodes from 'event-system/codes/internal';

import externalEventCodes from 'event-system/codes/external';

/**
 * Every render method shall have one unmount method and update method
 * 
 * 
 * 
 * */

import * as meetingStatuses from 'constants/meeting-statuses';
import * as meetingLiveStatuses from 'constants/meeting-live-statuses';

import CreatedView from '../created-view';
import ViewerMode from '../live-view/viewer-mode';
import EndedView from '../ended-view';
import ProducerModeEndedView from '../streamer/ended-view';

import styleAsString from '../styles/index.scss?inline';

class SingleProducerMeetingViewer extends HTMLElement {

  constructor() {

    super();

    this.attachShadow({ mode: 'open' });

    this.currentUserId = null;
    this.postId = null;
    this.post = null;
    this.meeting = null;
    this.loaded = false;
    this.autoPlay = false;

    // elements
    this.spinner = null;
    this.createdView = null;
    this.liveView = null;
    this.endedView = null;

    if (application.isAuthenticated() === true) {
      this.currentUserId = application.getUser()._id;
    }

    mount(this.shadowRoot, this.getStyleEl());

  }

  connectedCallback() {

    this.postId = this.getAttribute('postid');

    this.initialize();
    this.registerEventListeners(this.postId);

  }

  disconnectedCallback() {

    this.unregisterEventListeners();
    this.liveView.releaseResources();

    if (this.meeting !== null) {
      this.unmountCurrentMount();
    }

  }

  registerEventListeners(postId) {

    function handleMeetingLiveEvent(data) {

      this.meeting = data.meeting;
      this.liveView.update(this.meeting);

      this.unmountPreviousMount();
      this.mountRoot();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_LIVE}:${postId}`, handleMeetingLiveEvent.bind(this));

    function handleMeetingLiveStatusChangedEvent(data) {

      this.meeting = data.meeting;

      if (this.liveView === null) {
        return;
      }

      application.eventSystem.internal.emit(internalEventCodes.HIDE_MEETING_ATTENDEES_LIST, {});
      this.liveView.update(this.meeting);

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.MEETING_LIVE_STATUS_CHANGED}:${postId}`, handleMeetingLiveStatusChangedEvent.bind(this));

    function handleMeetingEndedEvent(data) {

      this.meeting = data.meeting;

      application.eventSystem.internal.emit(internalEventCodes.HIDE_MEETING_ATTENDEES_LIST, {});

      this.unmountPreviousMount();
      this.displayEndedView();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_ENDED}:${postId}`, handleMeetingEndedEvent.bind(this));

  }

  unregisterEventListeners() {

    application.eventSystem.internal.offAllOn(this);

  }

  getStyleEl() {

    const styleCode = `

      ${styleAsString}

    `;

    const style = el('style', styleCode);

    return style;

  }

  handleMeetingEndedEvent() {

    this.unmountPreviousMount();
    this.mountRoot();

  }

  async loadMeeting() {

    this.meeting = await application.stores.singleProducerMeetings.getSingleProducerMeetingByPostId(this.postId);

    this.loaded = true;

  }

  async loadPost() {

    this.post = await application.stores.posts.getPostBasicDetailsById(this.postId);
    this.post.owner = await application.usersStore.getUserById(this.post.ownerId);

  }

  async initialize() {

    this.mountSpinner();

    await Promise.all([this.loadMeeting(), this.loadPost()]);

    if (this.meeting.deleted === true) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Meeting is deleted',
        seconds: 5,
        type: 'error'
      });
      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: '/community'
      });
      return;
    }

    this.unmountSpinner();

    this.createdView = new CreatedView(this.currentUserId, this.meeting);
    this.liveView = new ViewerMode(this.currentUserId, this.meeting, this.post, isDisplayedInLiveView(this));
    this.endedView = new EndedView(this.meeting);

    this.mountRoot();

  }

  mountSpinner() {

    this.spinner = el('ht-spinner', {
      size: 'small'
    });

    mount(this.shadowRoot, this.spinner);

  }

  unmountSpinner() {

    if (this.spinner === null) {
      return;
    }

    unmount(this.shadowRoot, this.spinner);

  }

  mountLiveView() {

    if (this.liveView === null) {
      return;
    }

    mount(this.shadowRoot, this.liveView);

  }

  unmountLiveView() {

    if (this.liveView === null) {
      return;
    }

    unmount(this.shadowRoot, this.liveView);

  }

  mountEndedView() {

    mount(this.shadowRoot, this.endedView);

    if (this.autoPlay === true) {
      this.endedView.playVideo();
    }

  }

  unmountEndedView() {

    unmount(this.shadowRoot, this.endedView);

  }

  mountCreatedView() {

    mount(this.shadowRoot, this.createdView);

  }

  unmountCreatedView() {

    if (this.createdView === null) {
      return;
    }

    unmount(this.shadowRoot, this.createdView);

  }

  mountRoot() {

    if (this.meeting.status === meetingStatuses.CREATED) {
      this.mountCreatedView();
    } else if (this.meeting.status === meetingStatuses.LIVE) {
      this.mountLiveView();
    } else if (isDisplayedInLiveView(this) === true) {
      this.mountLiveStreamingEndedView();
    } else {
      this.mountEndedView();
    }

  }

  unmountPreviousMount() {

    if (this.meeting.status === meetingStatuses.LIVE) {
      this.unmountCreatedView();
    } else if (this.meeting.status === meetingStatuses.ENDED) {
      this.unmountLiveView();
    }

  }

  unmountCurrentMount() {

    if (this.meeting.status === meetingStatuses.CREATED && this.createdView !== null) {
      this.unmountCreatedView();
    } else if (this.meeting.status === meetingStatuses.LIVE && this.liveView !== null) {
      this.unmountLiveView();
    } else if (this.meeting.status === meetingStatuses.ENDED && this.endedView !== null) {
      this.unmountEndedView();
    }

  }

  handleMeetingWentLive() {

    this.meeting.status = meetingStatuses.LIVE;
    this.meeting.liveStatus = meetingLiveStatuses.CONNECTED;

    this.unmountCreatedView();
    this.mountLiveView();

  }

  mountLiveStreamingEndedView(labelTextPrefix) {

    mount(this.shadowRoot, new ProducerModeEndedView(labelTextPrefix, this.post));

  }

  displayEndedView() {

    if (isDisplayedInLiveView(this) === true) {
      this.mountLiveStreamingEndedView('Streaming is ended');
    } else {
      this.mountEndedView();
    }
  }

  visible() {

    if (this.endedView === null) {
      return;
    }

    this.endedView.playVideo();

  }

  invisible() {

    if (this.endedView === null) {
      return;
    }

    this.endedView.stopVideo();

  }

  play() {

    this.autoPlay = true;

    if (this.endedView === null) {
      return;
    }

    this.endedView.playVideo();

  }

}

function isDisplayedInLiveView(element) {

  const liveStreamingView = document.getElementById('live-streaming-view-main');

  if (liveStreamingView === null) {
    return false;
  }

  let _element = element;

  while (_element.parentNode) {
    if (_element.parentNode === liveStreamingView) {
      return true;
    }
    _element = _element.parentNode;
  }

  return false;

}

export default SingleProducerMeetingViewer;